import React from 'react';
import {
  VStack,
  HStack,
  FormControl,
  FormLabel,
  Input,
  NumberInput,
  NumberInputField,
  Switch,
  Select,
  FormHelperText,
  InputGroup,
  InputRightElement,
  IconButton,
  useDisclosure,
  Text,
  Link,
  Box,
} from '@chakra-ui/react';
import { ViewIcon, ViewOffIcon, ExternalLinkIcon } from '@chakra-ui/icons';

interface WhatsAppChannelConfigProps {
  config: Record<string, any>;
  onChange: (config: Record<string, any>) => void;
}

export const WhatsAppChannelConfig: React.FC<WhatsAppChannelConfigProps> = ({
  config,
  onChange,
}) => {
  const { isOpen: showToken, onToggle: toggleToken } = useDisclosure();

  const updateConfig = (key: string, value: any) => {
    onChange({
      ...config,
      [key]: value,
    });
  };

  return (
    <VStack spacing={4} align="stretch">
      {/* Setup Information */}
      <Box>
        <Text fontSize="sm" color="gray.600" mb={3}>
          To use WhatsApp Business API, you need to set up a{' '}
          <Link
            href="https://developers.facebook.com/docs/whatsapp/getting-started"
            color="blue.500"
            isExternal
          >
            WhatsApp Business Account <ExternalLinkIcon mx="2px" />
          </Link>
          {' '}through Meta for Developers.
        </Text>
      </Box>

      {/* Authentication */}
      <FormControl isRequired>
        <FormLabel>Access Token</FormLabel>
        <InputGroup>
          <Input
            type={showToken ? 'text' : 'password'}
            value={config.accessToken || ''}
            onChange={(e) => updateConfig('accessToken', e.target.value)}
            placeholder="EAAxxxxxxxxxxxxxxxxx"
            fontFamily="mono"
          />
          <InputRightElement>
            <IconButton
              aria-label={showToken ? 'Hide token' : 'Show token'}
              icon={showToken ? <ViewOffIcon /> : <ViewIcon />}
              variant="ghost"
              size="sm"
              onClick={toggleToken}
            />
          </InputRightElement>
        </InputGroup>
        <FormHelperText>
          Your WhatsApp Business API access token
        </FormHelperText>
      </FormControl>

      <FormControl isRequired>
        <FormLabel>Phone Number ID</FormLabel>
        <Input
          value={config.phoneNumberId || ''}
          onChange={(e) => updateConfig('phoneNumberId', e.target.value)}
          placeholder="1234567890123456"
          fontFamily="mono"
        />
        <FormHelperText>
          The ID of your WhatsApp Business phone number
        </FormHelperText>
      </FormControl>

      <FormControl>
        <FormLabel>Business Account ID</FormLabel>
        <Input
          value={config.businessAccountId || ''}
          onChange={(e) => updateConfig('businessAccountId', e.target.value)}
          placeholder="1234567890123456"
          fontFamily="mono"
        />
        <FormHelperText>
          Your WhatsApp Business Account ID (optional)
        </FormHelperText>
      </FormControl>

      {/* API Configuration */}
      <FormControl>
        <FormLabel>API Base URL</FormLabel>
        <Input
          value={config.baseUrl || 'https://graph.facebook.com'}
          onChange={(e) => updateConfig('baseUrl', e.target.value)}
          placeholder="https://graph.facebook.com"
        />
      </FormControl>

      <FormControl>
        <FormLabel>API Version</FormLabel>
        <Select
          value={config.apiVersion || 'v17.0'}
          onChange={(e) => updateConfig('apiVersion', e.target.value)}
        >
          <option value="v17.0">v17.0 (Current)</option>
          <option value="v16.0">v16.0</option>
          <option value="v15.0">v15.0</option>
        </Select>
      </FormControl>

      {/* Rate Limiting */}
      <HStack spacing={4}>
        <FormControl>
          <FormLabel>Rate Limit (msg/sec)</FormLabel>
          <NumberInput
            value={config.rateLimit || 20}
            onChange={(_, value) => updateConfig('rateLimit', value || 20)}
            min={1}
            max={100}
          >
            <NumberInputField />
          </NumberInput>
          <FormHelperText>
            WhatsApp allows 20 messages per second
          </FormHelperText>
        </FormControl>

        <FormControl>
          <FormLabel>Burst Limit</FormLabel>
          <NumberInput
            value={config.burstLimit || 50}
            onChange={(_, value) => updateConfig('burstLimit', value || 50)}
            min={1}
            max={500}
          >
            <NumberInputField />
          </NumberInput>
        </FormControl>
      </HStack>

      {/* Connection Settings */}
      <HStack spacing={4}>
        <FormControl>
          <FormLabel>Timeout (seconds)</FormLabel>
          <NumberInput
            value={config.timeout || 30}
            onChange={(_, value) => updateConfig('timeout', value || 30)}
            min={5}
            max={300}
          >
            <NumberInputField />
          </NumberInput>
        </FormControl>

        <FormControl>
          <FormLabel>Max Retries</FormLabel>
          <NumberInput
            value={config.maxRetries || 3}
            onChange={(_, value) => updateConfig('maxRetries', value || 3)}
            min={0}
            max={10}
          >
            <NumberInputField />
          </NumberInput>
        </FormControl>
      </HStack>

      {/* Media Settings */}
      <FormControl>
        <FormLabel>Max Media Size (MB)</FormLabel>
        <NumberInput
          value={config.maxMediaSize ? config.maxMediaSize / (1024 * 1024) : 16}
          onChange={(_, value) => updateConfig('maxMediaSize', (value || 16) * 1024 * 1024)}
          min={1}
          max={100}
        >
          <NumberInputField />
        </NumberInput>
        <FormHelperText>
          Maximum file size for media uploads
        </FormHelperText>
      </FormControl>

      {/* Webhook Settings */}
      <FormControl>
        <FormLabel>Webhook URL</FormLabel>
        <Input
          value={config.webhookUrl || ''}
          onChange={(e) => updateConfig('webhookUrl', e.target.value)}
          placeholder="https://your-domain.com/webhooks/whatsapp"
        />
        <FormHelperText>
          URL to receive webhook events (optional)
        </FormHelperText>
      </FormControl>

      <FormControl>
        <FormLabel>Webhook Secret</FormLabel>
        <Input
          value={config.webhookSecret || ''}
          onChange={(e) => updateConfig('webhookSecret', e.target.value)}
          placeholder="your-webhook-secret"
          type="password"
        />
        <FormHelperText>
          Secret for webhook verification (optional)
        </FormHelperText>
      </FormControl>

      {/* Template Settings */}
      <FormControl>
        <FormLabel>Default Template Language</FormLabel>
        <Select
          value={config.defaultLanguage || 'en'}
          onChange={(e) => updateConfig('defaultLanguage', e.target.value)}
        >
          <option value="en">English</option>
          <option value="ru">Russian</option>
          <option value="es">Spanish</option>
          <option value="fr">French</option>
          <option value="de">German</option>
        </Select>
      </FormControl>

      {/* Business Settings */}
      <FormControl>
        <FormLabel>Business Profile</FormLabel>
        <Switch
          isChecked={config.enableBusinessProfile !== false}
          onChange={(e) => updateConfig('enableBusinessProfile', e.target.checked)}
          colorScheme="primary"
        />
        <FormHelperText>
          Show business profile information in messages
        </FormHelperText>
      </FormControl>
    </VStack>
  );
};